/**
 * Error Boundary Component
 *
 * Catches unhandled JavaScript errors in child components and displays
 * the shared full-screen PageError panel (Alert + Retry, "Go to dashboard").
 */

import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';
import { logger } from '../../utils/logger';
import { getRouterRef } from '../../lib/routerRef';
import { PageError } from './PageError';

interface Props {
  children: ReactNode;
  /**
   * Shown in place of the children after an error. A function gets `retry`,
   * which clears the error and renders the children again (pair it with
   * TanStack's `useQueryErrorResetBoundary().reset` for a suspense query).
   */
  fallback?: ReactNode | ((retry: () => void) => ReactNode);
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });
    logger.error('ErrorBoundary caught an error', error, {
      context: { componentStack: errorInfo.componentStack },
    });
  }

  handleReload = (): void => { window.location.reload(); };

  handleGoHome = (): void => {
    const router = getRouterRef();
    if (router) {
      router.navigate({ to: '/admin' });
    } else {
      window.location.href = '/admin';
    }
  };

  handleRetry  = (): void => { this.setState({ hasError: false, error: null, errorInfo: null }); };

  render(): ReactNode {
    if (this.state.hasError) {
      const { fallback } = this.props;
      if (typeof fallback === 'function') return fallback(this.handleRetry);
      if (fallback) return fallback;

      return (
        <PageError
          error={this.state.error}
          title="Something went wrong"
          onRetry={this.handleRetry}
          onGoHome={this.handleGoHome}
          details={
            this.state.error
              ? `${this.state.error.toString()}${this.state.errorInfo?.componentStack ?? ''}`
              : undefined
          }
          fullScreen
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
